import { JourneyElementsDto } from 'Global/JourneyElements/Types/JourneyElementsDto';
import { getValidDestinationLocations } from 'Global/JourneyElements/Utils/getValidDestinationLocations';
import { isNotNullOrUndefined } from '../../../../../Util/TypeGuards';

export interface TripSelectorState {
	startLocationIds: string[];
	startLocationId: string;
	endLocationId: string;
	possibleDestinationIds: string[];
}

export enum TripSelectorActionType {
	ChangeStartLocation = 'change-start-location',
	ChangeEndLocation = 'change-end-location',
	SwitchLocations = 'switch-locations',
}

type ChangeStartLocationActionEvent = {
	type: TripSelectorActionType.ChangeStartLocation;
	journeyElements: JourneyElementsDto;
	orgId?: string | null;
	startLocationId: string;
};

export type TripSelectorActionEvent =
	| ChangeStartLocationActionEvent
	| { type: TripSelectorActionType.ChangeEndLocation; endLocationId: string }
	| { type: TripSelectorActionType.SwitchLocations; journeyElements: JourneyElementsDto; orgId?: string | null };

export const tripSelectorReducer = (state: TripSelectorState, action: TripSelectorActionEvent): TripSelectorState => {
	switch (action.type) {
		case TripSelectorActionType.ChangeStartLocation: {
			const { journeyElements, orgId, startLocationId } = action;
			return processTripSelectorState(journeyElements, startLocationId, state.endLocationId, orgId);
		}
		case TripSelectorActionType.ChangeEndLocation: {
			const { endLocationId } = action;
			return {
				...state,
				endLocationId,
			};
		}
		case TripSelectorActionType.SwitchLocations: {
			const { journeyElements, orgId } = action;
			const { startLocationId: endLocationId, endLocationId: startLocationId } = state;
			return processTripSelectorState(journeyElements, startLocationId, endLocationId, orgId);
		}

		default:
			return state;
	}
};

export const processTripSelectorState = (
	journeyElements: JourneyElementsDto,
	startLocationId: string,
	endLocationId: string,
	orgId?: string | null,
): TripSelectorState => {
	const { routes, locations } = journeyElements;

	let startLocId = startLocationId;
	let endLocId = endLocationId;
	let departureIds = locations.map(x => x.id);
	let destinationIds = locations.map(x => x.id);

	const allowedRoutes = isNotNullOrUndefined(orgId) ? routes.filter(x => x.organisationIds?.includes(orgId)) : routes;

	const allowedLocationIds = new Set<string>();

	for (const route of allowedRoutes) {
		for (const stop of route.stops) {
			allowedLocationIds.add(stop.locationId);
		}
	}

	if (allowedLocationIds.size === 0) {
		return {
			startLocationId: '',
			endLocationId: '',
			startLocationIds: [],
			possibleDestinationIds: [],
		};
	}

	departureIds = Array.from(allowedLocationIds);

	// Ensure start locations is valid
	if (!allowedLocationIds.has(startLocId)) {
		const [firstLocationId] = Array.from(allowedLocationIds);
		startLocId = firstLocationId;
	}

	destinationIds = getValidDestinationLocations(journeyElements, startLocId, orgId).map(x => x.id);

	// A start location with nothing sailing onward from it leaves nothing to offer as a destination.
	// Rather than hand back a half-formed pair, move the start to a location that does lead
	// somewhere — which is what pressing the flip button was already doing by hand.
	if (destinationIds.length === 0) {
		for (const candidateStartId of departureIds) {
			const candidateDestinationIds = getValidDestinationLocations(
				journeyElements,
				candidateStartId,
				orgId,
			).map(x => x.id);

			if (candidateDestinationIds.length > 0) {
				startLocId = candidateStartId;
				destinationIds = candidateDestinationIds;
				break;
			}
		}
	}

	// Ensure end location is valid.
	//
	// This used to read `const [firstDestinationId] = Array.from(destinationIds)` inside a try/catch.
	// Destructuring an empty array does not throw, so the catch never ran and `endLocId` was simply
	// set to `undefined` — a value the declared `string` type hid. TripSelector's mount effect then
	// pushed it into `wizardData.toLocationId`, which is how the tickets tab ended up with a blank
	// "Somewhere to ___" heading and a trips request the server could not answer.
	if (!destinationIds.includes(endLocId)) {
		const [firstDestinationId] = destinationIds;
		if (firstDestinationId === undefined) {
			// No allowed location leads anywhere at all. Empty rather than undefined, so the guards
			// downstream (which test for an empty string) can see it.
			console.error('No available destination for any start location');
		}
		endLocId = firstDestinationId ?? '';
	}

	return {
		startLocationIds: departureIds,
		startLocationId: startLocId,
		endLocationId: endLocId,
		possibleDestinationIds: destinationIds,
	};
};

export const isTravelPossible = (
	journeyElements: JourneyElementsDto,
	startLocationId: string,
	endLocationId: string,
	orgId?: string | null,
): boolean => {
	const destinations = getValidDestinationLocations(journeyElements, startLocationId, orgId);
	return !!destinations.find(x => x.id === endLocationId);
};
